import {
  Collapsible as BaseCollapsible,
  type CollapsiblePanelProps as BaseCollapsiblePanelProps,
  type CollapsibleRootProps as BaseCollapsibleRootProps,
  type CollapsibleTriggerProps as BaseCollapsibleTriggerProps,
} from "@base-ui/react/collapsible";
import classNames from "classnames";
import { ChevronDown } from "lucide-react";
import * as React from "react";

import styles from "./Collapsible.module.css";

/**
 * Narrows Base UI's `className`, which it types as a string *or* a function of
 * the part's state, to the string form: a state-dependent class puts a styling
 * decision at the call site that belongs in the module. `ref` is dropped
 * because `forwardRef` re-adds it.
 */
type PartProps<P> = Omit<P, "className" | "ref"> & { className?: string };

export type CollapsibleRootProps = PartProps<BaseCollapsibleRootProps>;
export type CollapsibleTriggerProps = PartProps<BaseCollapsibleTriggerProps>;
export type CollapsiblePanelProps = PartProps<BaseCollapsiblePanelProps>;

/** The bordered box; owns the open state (`open` or `defaultOpen`). */
const Root = React.forwardRef<HTMLDivElement, CollapsibleRootProps>(
  function CollapsibleRoot({ className, ...props }, ref) {
    return (
      <BaseCollapsible.Root
        ref={ref}
        className={classNames(styles.root, className)}
        {...props}
      />
    );
  },
);

/**
 * The full-width button that opens and closes the panel, led by a chevron.
 * Wrap it in a heading when the section belongs in the page outline.
 */
const Trigger = React.forwardRef<HTMLButtonElement, CollapsibleTriggerProps>(
  function CollapsibleTrigger({ className, children, ...props }, ref) {
    return (
      <BaseCollapsible.Trigger
        ref={ref}
        className={classNames(styles.trigger, className)}
        {...props}
      >
        <ChevronDown aria-hidden className={styles.chevron} />
        {children}
      </BaseCollapsible.Trigger>
    );
  },
);

/** The contents, ruled off from the trigger. */
const Panel = React.forwardRef<HTMLDivElement, CollapsiblePanelProps>(
  function CollapsiblePanel({ className, ...props }, ref) {
    return (
      <BaseCollapsible.Panel
        ref={ref}
        className={classNames(styles.panel, className)}
        {...props}
      />
    );
  },
);

/**
 * A section that opens and closes, styled like a form section. The parts and
 * props are Base UI's Collapsible; `Collapsible` is itself `Collapsible.Root`,
 * the way `Field` is its own root.
 *
 * @example
 * ```tsx
 * <Collapsible.Root>
 *   <h4>
 *     <Collapsible.Trigger>Advanced settings</Collapsible.Trigger>
 *   </h4>
 *   <Collapsible.Panel>…</Collapsible.Panel>
 * </Collapsible.Root>
 * ```
 */
export const Collapsible = Object.assign(Root, { Root, Trigger, Panel });
